import { differenceInCalendarDays, format, isSameDay, isSameYear, isToday, isYesterday } from 'date-fns';

/** `10:30 AM` — the timestamp beside a sender name. */
export function formatClock(iso: string): string {
  return format(new Date(iso), 'h:mm a');
}

/**
 * `1:19` — the hover-only stamp in a grouped message's gutter.
 *
 * No meridiem: the gutter is one avatar wide (32px) and `1:19 PM` would either
 * clip or push the bubble column across. The group header directly above always
 * carries the full `h:mm a`, so the short form is never ambiguous.
 */
export function formatClockShort(iso: string): string {
  return format(new Date(iso), 'h:mm');
}

/**
 * `Today` · `Yesterday` · `Friday` · `Wednesday, 12 February` · `14 July 2024`
 * — the timeline day separator, matching how Teams breaks a thread.
 *
 * Inside the last week a bare weekday is enough to place a message, and it
 * reads far faster than a full date; past that the month is needed, and past
 * the year boundary so is the year.
 */
export function formatDayDivider(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  const daysAgo = differenceInCalendarDays(now, date);
  if (daysAgo > 0 && daysAgo < 7) return format(date, 'EEEE');
  if (isSameYear(date, now)) return format(date, 'EEEE, d MMMM');
  return format(date, 'd MMMM yyyy');
}

/** `10:30 AM` today, `Yesterday`, `Tue`, `14/07/25` — the DM list meta column. */
export function formatListStamp(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return 'Yesterday';
  const now = new Date();
  const withinWeek = now.getTime() - date.getTime() < 6 * 24 * 60 * 60 * 1000;
  if (withinWeek) return format(date, 'EEE');
  return isSameYear(date, now) ? format(date, 'd MMM') : format(date, 'dd/MM/yy');
}

/** `now` · `2m` · `4h` · `3d` — compact relative age for popups and receipts. */
export function formatAge(iso: string): string {
  const deltaMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/** `1:04` / `12:07` — voice-clip and recording duration. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** `Tomorrow 09:00` — the scheduled-send confirmation line. */
export function formatSchedule(iso: string): string {
  const date = new Date(iso);
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  if (isToday(date)) return `Today ${format(date, 'HH:mm')}`;
  if (isSameDay(date, tomorrow)) return `Tomorrow ${format(date, 'HH:mm')}`;
  return format(date, 'd MMM, HH:mm');
}

export function isSameDayIso(a: string, b: string): boolean {
  return isSameDay(new Date(a), new Date(b));
}
